import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";

const { Text } = Typography;

const plans = ["free", "plus", "pro", "pro_max", "business"] as const;
const planLabels: Record<(typeof plans)[number], string> = {
  free: "免费",
  plus: "历史 Plus（兼容）",
  pro: "Pro",
  pro_max: "Pro Max",
  business: "商业版",
};

interface PolicyRow {
  key: string;
  label: string;
  kind: "limit" | "feature";
  defaultPeriod?: "daily" | "weekly" | "monthly" | "total";
}

const policyRows: PolicyRow[] = [
  { key: "chat_daily", label: "对话次数", kind: "limit", defaultPeriod: "daily" },
  { key: "agent_run_daily", label: "Agent 运行", kind: "limit", defaultPeriod: "daily" },
  { key: "workflow_run_daily", label: "工作流运行", kind: "limit", defaultPeriod: "daily" },
  { key: "mcp_tool_weekly", label: "MCP 工具调用", kind: "limit", defaultPeriod: "weekly" },
  { key: "summary_monthly", label: "摘要次数", kind: "limit", defaultPeriod: "monthly" },
  { key: "podcast_minutes_monthly", label: "播客分钟", kind: "limit", defaultPeriod: "monthly" },
  { key: "kb_index_monthly", label: "知识入库", kind: "limit", defaultPeriod: "monthly" },
  { key: "knowledge_entries", label: "知识条目", kind: "limit", defaultPeriod: "total" },
  { key: "projects", label: "项目数量", kind: "limit", defaultPeriod: "total" },
  { key: "agents_per_project", label: "每项目 Agent", kind: "limit", defaultPeriod: "total" },
  { key: "plan_token_monthly", label: "赠送 Token", kind: "limit", defaultPeriod: "monthly" },
  { key: "analytics_sites", label: "分析站点", kind: "limit", defaultPeriod: "total" },
  { key: "analytics_goals", label: "分析目标", kind: "limit", defaultPeriod: "total" },
  { key: "analytics_events_monthly", label: "分析事件", kind: "limit", defaultPeriod: "monthly" },
  { key: "analytics_reports_monthly", label: "AI 分析报告", kind: "limit", defaultPeriod: "monthly" },
  { key: "tool_calls", label: "工具调用能力", kind: "feature" },
  { key: "custom_models", label: "自定义模型", kind: "feature" },
  { key: "site_extensions", label: "站点扩展能力", kind: "feature" },
];

type PlanSettings = Record<string, number | boolean>;
export interface PlanPolicyFormValues {
  _timezone?: string;
  _periods?: Record<string, string>;
  free?: PlanSettings;
  plus?: PlanSettings;
  pro?: PlanSettings;
  pro_max?: PlanSettings;
  business?: PlanSettings;
}

export const buildPlanPolicyPayload = (values: PlanPolicyFormValues): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    _timezone: (values._timezone || "Asia/Shanghai").trim(),
    _periods: values._periods || {},
  };
  plans.forEach((plan) => {
    payload[plan] = values[plan] || {};
  });
  return payload;
};

const PlanPolicySettings: React.FC = () => {
  const { API_BASE_URL, token } = useGlobal();
  const [form] = Form.useForm<PlanPolicyFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyingDefaults, setApplyingDefaults] = useState(false);
  // 重置用量
  const [resetOpen, setResetOpen] = useState(false);
  const [resetScope, setResetScope] = useState<"all" | "daily" | "weekly" | "monthly">("monthly");
  const [resetUserId, setResetUserId] = useState<string>("");
  const [resetting, setResetting] = useState(false);
  // 单用户配额覆盖
  const [ovUserId, setOvUserId] = useState<string>("");
  const [ovRows, setOvRows] = useState<Record<string, number | undefined>>({});
  const [ovLoading, setOvLoading] = useState(false);
  const [ovSaving, setOvSaving] = useState(false);
  const [ovLoadedUser, setOvLoadedUser] = useState<string>("");

  const loadPolicy = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await request.get(`${API_BASE_URL}/api/v3/admin/system/plan-quotas`);
      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "获取会员策略失败");
      }
      const data = response.data.data || {};
      const periods = { ...(data._periods || {}) };
      policyRows.forEach((row) => {
        if (row.defaultPeriod && !periods[row.key]) periods[row.key] = row.defaultPeriod;
      });
      form.setFieldsValue({ ...data, _timezone: data._timezone || "Asia/Shanghai", _periods: periods });
    } catch (error: any) {
      message.error(error.response?.data?.msg || error.message || "获取会员策略失败");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, form, token]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  const savePolicy = useCallback(async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const response = await request.put(
        `${API_BASE_URL}/api/v3/admin/system/plan-quotas`,
        buildPlanPolicyPayload(values)
      );
      if (response.data.code !== 200) {
        throw new Error(response.data.msg || "保存会员策略失败");
      }
      form.setFieldsValue(response.data.data || values);
      message.success("会员与额度策略已生效");
    } catch (error: any) {
      message.error(error.response?.data?.msg || error.message || "保存会员策略失败");
    } finally {
      setSaving(false);
    }
  }, [API_BASE_URL, form]);

  const applyDefaults = useCallback((overwrite: boolean) => {
    Modal.confirm({
      title: overwrite ? "用推荐默认覆盖" : "应用推荐默认（仅填补缺失）",
      content: overwrite
        ? "会用推荐默认覆盖涉及到的额度项，已有配置将被替换。"
        : "只为尚未配置的额度项填入推荐默认值，不改动你已设置的值。",
      okText: "确认应用",
      onOk: async () => {
        setApplyingDefaults(true);
        try {
          const res = await request.post(`${API_BASE_URL}/api/v3/admin/system/plan-quotas/defaults`, { overwrite });
          if (res.data.code !== 200) throw new Error(res.data.msg || "应用失败");
          message.success("已应用推荐默认");
          await loadPolicy();
        } catch (e: any) {
          message.error(e.response?.data?.msg || e.message || "应用失败");
        } finally {
          setApplyingDefaults(false);
        }
      },
    });
  }, [API_BASE_URL, loadPolicy]);

  const doReset = useCallback(async () => {
    setResetting(true);
    try {
      const body: Record<string, unknown> = { scope: resetScope };
      const uid = Number(resetUserId.trim());
      if (resetUserId.trim() && Number.isFinite(uid) && uid > 0) body.userId = uid;
      const res = await request.post(`${API_BASE_URL}/api/v3/admin/system/plan-quotas/reset-usage`, body);
      if (res.data.code !== 200) throw new Error(res.data.msg || "重置失败");
      message.success(`已重置 ${res.data.data?.deleted ?? 0} 个计数`);
      setResetOpen(false);
    } catch (e: any) {
      message.error(e.response?.data?.msg || e.message || "重置失败");
    } finally {
      setResetting(false);
    }
  }, [API_BASE_URL, resetScope, resetUserId]);

  const loadOverrides = useCallback(async () => {
    const uid = Number(ovUserId.trim());
    if (!ovUserId.trim() || !Number.isFinite(uid) || uid <= 0) {
      message.warning("请输入有效的用户 ID");
      return;
    }
    setOvLoading(true);
    try {
      const res = await request.get(`${API_BASE_URL}/api/v3/admin/system/plan-quotas/user-overrides`, { params: { userId: uid } });
      if (res.data.code !== 200) throw new Error(res.data.msg || "读取失败");
      setOvRows((res.data.data?.overrides as Record<string, number>) || {});
      setOvLoadedUser(String(uid));
    } catch (e: any) {
      message.error(e.response?.data?.msg || e.message || "读取失败");
    } finally {
      setOvLoading(false);
    }
  }, [API_BASE_URL, ovUserId]);

  const saveOverrides = useCallback(async () => {
    const uid = Number(ovLoadedUser);
    if (!Number.isFinite(uid) || uid <= 0) {
      message.warning("请先读取某个用户的覆盖");
      return;
    }
    const overrides: Record<string, number> = {};
    Object.entries(ovRows).forEach(([k, v]) => {
      if (typeof v === "number" && v >= 0) overrides[k] = v;
    });
    setOvSaving(true);
    try {
      const res = await request.put(`${API_BASE_URL}/api/v3/admin/system/plan-quotas/user-overrides`, { userId: uid, overrides });
      if (res.data.code !== 200) throw new Error(res.data.msg || "保存失败");
      setOvRows((res.data.data?.overrides as Record<string, number>) || {});
      message.success("单用户配额覆盖已保存");
    } catch (e: any) {
      message.error(e.response?.data?.msg || e.message || "保存失败");
    } finally {
      setOvSaving(false);
    }
  }, [API_BASE_URL, ovLoadedUser, ovRows]);

  // 商业版定制报价：为指定用户创建定向商品（仅该用户可见可购）。
  const [offerUserId, setOfferUserId] = useState("");
  const [offerTitle, setOfferTitle] = useState("");
  const [offerAmountYuan, setOfferAmountYuan] = useState<number | null>(null);
  const [offerDays, setOfferDays] = useState<number | null>(365);
  const [offerSaving, setOfferSaving] = useState(false);

  const createBusinessOffer = useCallback(async () => {
    const uid = Number(offerUserId.trim());
    if (!Number.isFinite(uid) || uid <= 0) {
      message.warning("请输入有效的用户 ID");
      return;
    }
    if (!offerAmountYuan || offerAmountYuan <= 0) {
      message.warning("请输入定制金额");
      return;
    }
    if (!offerDays || offerDays <= 0) {
      message.warning("请输入会员天数");
      return;
    }
    setOfferSaving(true);
    try {
      const res = await request.post(`${API_BASE_URL}/api/v3/admin/business-offers`, {
        userId: uid,
        title: offerTitle.trim(),
        amountCents: Math.round(offerAmountYuan * 100),
        vipDays: offerDays,
      });
      if (res.data.code !== 200) throw new Error(res.data.msg || "创建失败");
      message.success(`定向报价已创建（商品 #${res.data.data?.id ?? "?"}），用户 #${uid} 在商品列表可见并可支付`);
      setOfferUserId("");
      setOfferTitle("");
      setOfferAmountYuan(null);
    } catch (e: any) {
      message.error(e.response?.data?.msg || e.message || "创建失败");
    } finally {
      setOfferSaving(false);
    }
  }, [API_BASE_URL, offerUserId, offerTitle, offerAmountYuan, offerDays]);

  const columns = useMemo(
    () => [
      {
        title: "能力 / 额度",
        dataIndex: "label",
        key: "label",
        fixed: "left" as const,
        width: 150,
        render: (label: string, row: PolicyRow) => (
          <Space direction="vertical" size={0}>
            <Text strong>{label}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{row.key}</Text>
          </Space>
        ),
      },
      {
        title: "周期",
        key: "period",
        width: 125,
        render: (_: unknown, row: PolicyRow) =>
          row.kind === "limit" ? (
            <Form.Item name={["_periods", row.key]} noStyle>
              <Select
                style={{ width: 112 }}
                options={[
                  { value: "daily", label: "每日" },
                  { value: "weekly", label: "每周" },
                  { value: "monthly", label: "每月" },
                  { value: "total", label: "总量" },
                ]}
              />
            </Form.Item>
          ) : <Text type="secondary">功能开关</Text>,
      },
      ...plans.map((plan) => ({
        title: planLabels[plan],
        key: plan,
        width: 125,
        render: (_: unknown, row: PolicyRow) => (
          <Form.Item name={[plan, row.key]} noStyle valuePropName={row.kind === "feature" ? "checked" : "value"}>
            {row.kind === "feature" ? (
              <Switch checkedChildren="开" unCheckedChildren="关" />
            ) : (
              <InputNumber min={0} precision={0} style={{ width: 108 }} placeholder="0 = 不限" />
            )}
          </Form.Item>
        ),
      })),
    ],
    []
  );

  return (
    <Card
      title="会员与额度策略"
      extra={
        <Space wrap>
          <Button loading={applyingDefaults} onClick={() => applyDefaults(false)}>应用推荐默认</Button>
          <Button danger onClick={() => setResetOpen(true)}>重置用量</Button>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={loadPolicy}>刷新</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={savePolicy}>保存并生效</Button>
        </Space>
      }
    >
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message="0 表示不限；Plus 知识条目最低为 10,000，后端会自动保护该下限。"
        />
        <Form form={form} layout="vertical" initialValues={{ _timezone: "Asia/Shanghai" }}>
          <Form.Item
            name="_timezone"
            label="额度结算时区"
            rules={[{ required: true, message: "请输入 IANA 时区" }]}
          >
            <Input style={{ maxWidth: 320 }} placeholder="Asia/Shanghai" />
          </Form.Item>
          <Table<PolicyRow>
            rowKey="key"
            columns={columns}
            dataSource={policyRows}
            loading={loading}
            pagination={false}
            scroll={{ x: 900 }}
            size="small"
          />
        </Form>

        <Card size="small" title="商业版定制报价（为指定用户创建专属付费商品）">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="创建后仅目标用户在商品列表可见并可支付；支付成功走既有交付链路升级为商业版（vip_level=3）。配额随后可在下方「单用户配额覆盖」单独调整。"
          />
          <Space wrap>
            <Input
              style={{ width: 160 }}
              placeholder="用户 ID"
              value={offerUserId}
              onChange={(e) => setOfferUserId(e.target.value)}
            />
            <Input
              style={{ width: 240 }}
              placeholder="报价名称（默认：商业版定制服务）"
              value={offerTitle}
              onChange={(e) => setOfferTitle(e.target.value)}
            />
            <InputNumber
              style={{ width: 160 }}
              placeholder="金额（元）"
              min={0.01}
              precision={2}
              value={offerAmountYuan}
              onChange={(v) => setOfferAmountYuan(v)}
            />
            <InputNumber
              style={{ width: 140 }}
              placeholder="会员天数"
              min={1}
              precision={0}
              value={offerDays}
              onChange={(v) => setOfferDays(v)}
            />
            <Button type="primary" loading={offerSaving} onClick={createBusinessOffer}>创建定向报价</Button>
          </Space>
        </Card>

        <Card size="small" title="单用户配额覆盖（商业版按用户单独设额度）">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="覆盖优先于套餐默认。留空=不覆盖（回落套餐）；填 0 = 对该用户此项不限。"
          />
          <Space wrap style={{ marginBottom: 12 }}>
            <Input
              style={{ width: 200 }}
              placeholder="用户 ID"
              value={ovUserId}
              onChange={(e) => setOvUserId(e.target.value)}
            />
            <Button loading={ovLoading} onClick={loadOverrides}>读取</Button>
            <Button type="primary" loading={ovSaving} disabled={!ovLoadedUser} onClick={saveOverrides}>保存覆盖</Button>
            {ovLoadedUser && <Text type="secondary">当前编辑用户 #{ovLoadedUser}</Text>}
          </Space>
          {ovLoadedUser && (
            <Table<PolicyRow>
              rowKey="key"
              size="small"
              pagination={false}
              scroll={{ x: 480 }}
              dataSource={policyRows.filter((r) => r.kind === "limit")}
              columns={[
                {
                  title: "额度项",
                  dataIndex: "label",
                  key: "label",
                  render: (label: string, row: PolicyRow) => (
                    <Space direction="vertical" size={0}>
                      <Text strong>{label}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{row.key}</Text>
                    </Space>
                  ),
                },
                {
                  title: "覆盖额度",
                  key: "override",
                  width: 180,
                  render: (_: unknown, row: PolicyRow) => (
                    <InputNumber
                      min={0}
                      precision={0}
                      style={{ width: 150 }}
                      placeholder="不覆盖"
                      value={ovRows[row.key]}
                      onChange={(v) => setOvRows((prev) => ({ ...prev, [row.key]: (v as number) ?? undefined }))}
                    />
                  ),
                },
              ]}
            />
          )}
        </Card>
      </Space>

      <Modal
        title="重置配额用量"
        open={resetOpen}
        onOk={doReset}
        confirmLoading={resetting}
        onCancel={() => setResetOpen(false)}
        okText="确认重置"
        okButtonProps={{ danger: true }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert type="warning" showIcon message="重置只清当期已用计数，不改配额配置。用户 ID 留空=重置所有用户（仅超级管理员可执行全体重置）。" />
          <div>
            <Text>范围：</Text>
            <Select
              style={{ width: 160, marginLeft: 8 }}
              value={resetScope}
              onChange={(v) => setResetScope(v)}
              options={[
                { value: "monthly", label: "月度用量" },
                { value: "weekly", label: "周用量" },
                { value: "daily", label: "日用量" },
                { value: "all", label: "全部" },
              ]}
            />
          </div>
          <Input
            style={{ maxWidth: 240 }}
            placeholder="用户 ID（留空=所有用户）"
            value={resetUserId}
            onChange={(e) => setResetUserId(e.target.value)}
          />
        </Space>
      </Modal>
    </Card>
  );
};

export default PlanPolicySettings;
